import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Plus, Trash2, Save, Pencil, X, DollarSign, Search, Wand2, Link2 } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "GBP", "EUR", "INR"];
const SYM = { USD: "$", GBP: "£", EUR: "€", INR: "₹" };
const GST_SLABS = [
  { value: "nil", label: "Nil (no GST)" },
  { value: "inclusive", label: "Inclusive (18% built in)" },
  { value: "5", label: "5% (exclusive)" },
  { value: "10", label: "10% (exclusive)" },
  { value: "15", label: "15% (exclusive)" },
  { value: "18", label: "18% (exclusive)" },
];
const GST_LABEL = Object.fromEntries(GST_SLABS.map(s => [s.value, s.label]));
const PROGRAMS = [
  "Volunteering", "Internship", "High School Exchange",
  "Student Group Tours", "Cultural Immersion", "Language Study", "Other",
];

const empty = () => ({
  partner_id: "", destination: "", city: "", program: "Volunteering",
  duration_weeks: 4, price: 0, currency: "USD", gst_slab: "nil",
});

export default function Pricing() {
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const [r, p, c] = await Promise.all([
      api.get("/pricing").catch(() => ({ data: [] })),
      api.get("/partner-accounts").catch(() => ({ data: [] })),
      api.get("/accommodations/countries").catch(() => ({ data: { countries: [] } })),
    ]);
    setRows(r.data || []);
    setPartners(p.data || []);
    setCountries(c.data?.countries || ["India", "Nepal", "Sri Lanka", "South Africa", "Kenya", "Thailand"]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      partner_id: r.partner_id, destination: r.destination || "",
      city: r.city || "", program: r.program || "Volunteering",
      duration_weeks: r.duration_weeks || 4, price: r.price || 0,
      currency: r.currency || "USD", gst_slab: r.gst_slab || "nil",
    });
    setOpen(true);
  };

  const cancel = () => { setOpen(false); setEditingId(null); setForm(empty()); };

  const submit = async () => {
    if (!form.partner_id) return toast.error("Pick a partner");
    if (!form.destination) return toast.error("Country required");
    if (!form.program) return toast.error("Program required");
    if (!form.price || form.price <= 0) return toast.error("Price must be > 0");
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        duration_weeks: parseInt(form.duration_weeks) || 0,
      };
      if (editingId) await api.patch(`/pricing/${editingId}`, payload);
      else await api.post("/pricing", payload);
      toast.success(editingId ? "Pricing updated" : "Pricing added");
      cancel();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Archive this pricing rule?")) return;
    try { await api.delete(`/pricing/${id}`); toast.success("Archived"); await load(); }
    catch { toast.error("Failed"); }
  };

  const scaffold = async () => {
    if (!window.confirm("Create a placeholder pricing row (price=0, USD, nil GST) for every combination of partner × destination × programme × duration that doesn't exist yet? Safe to re-run — dupes are skipped.")) return;
    try {
      const { data } = await api.post("/pricing/bulk-scaffold", {});
      toast.success(`Scaffolded ${data.created} new rules · skipped ${data.skipped} existing · across ${data.partners} partner${data.partners === 1 ? "" : "s"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scaffold failed"); }
  };

  const attributeOrphans = async () => {
    if (!window.confirm("Attribute every program booking with a missing partner? Uses linked applications first, then fuzzy name matching. Safe to re-run.")) return;
    try {
      const { data } = await api.post("/program-bookings/backfill-partner-attribution");
      toast.success(`Attributed ${data.matched_by_link} via linked apps + ${data.matched_by_name} via name match · ${data.remaining} still orphaned`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Attribution failed"); }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter(r =>
      (r.partner_name || "").toLowerCase().includes(s) ||
      (r.destination || "").toLowerCase().includes(s) ||
      (r.city || "").toLowerCase().includes(s) ||
      (r.program || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><DollarSign className="text-brand" size={22}/> Pricing</h1>
          <p className="text-xs text-[color:var(--aii-muted)]">Per-partner, per-destination pricing rules. Partners see the price on their applications automatically; Sales &amp; Invoices auto-populate on registration.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-[color:var(--aii-muted)]"/>
            <input data-testid="pricing-search" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)}
                   className="pl-7 pr-2 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white text-sm w-56"/>
          </div>
          <button onClick={attributeOrphans} data-testid="pricing-attribute"
            title="Attribute program bookings with a missing partner via linked applications + name match"
            className="text-sm px-3 py-2 border border-[color:var(--aii-border)] text-[color:var(--aii-text-1)] rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
            <Link2 size={13}/> Attribute orphans
          </button>
          <button onClick={scaffold} data-testid="pricing-scaffold"
            title="Bulk-create placeholder pricing rows for every partner × destination × programme × duration combo"
            className="text-sm px-3 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
            <Wand2 size={13}/> Scaffold matrix
          </button>
          <button onClick={() => { setOpen(true); setEditingId(null); setForm(empty()); }} data-testid="pricing-new"
            className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
            <Plus size={14}/> New rule
          </button>
        </div>
      </div>

      {open && (
        <div className="aii-card p-6 space-y-4" data-testid="pricing-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{editingId ? "Edit pricing rule" : "New pricing rule"}</h3>
            <button onClick={cancel}><X size={16}/></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Partner *">
              <select data-testid="pricing-partner" className="input-aii" value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })}>
                <option value="">Choose partner…</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Country *">
              <select data-testid="pricing-country" className="input-aii" value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })}>
                <option value="">Choose…</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="City (blank = any)">
              <input data-testid="pricing-city" className="input-aii" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Jaipur"/>
            </Field>
            <Field label="Program *">
              <select data-testid="pricing-program" className="input-aii" value={form.program} onChange={e => setForm({ ...form, program: e.target.value })}>
                {PROGRAMS.map(p => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Duration (weeks)">
              <input data-testid="pricing-duration" type="number" min={1} className="input-aii" value={form.duration_weeks} onChange={e => setForm({ ...form, duration_weeks: e.target.value })}/>
            </Field>
            <Field label="Price *">
              <input data-testid="pricing-price" type="number" min={0} className="input-aii" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}/>
            </Field>
            <Field label="Currency">
              <select data-testid="pricing-currency" className="input-aii" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="GST tax slab">
              <select data-testid="pricing-gst" className="input-aii" value={form.gst_slab} onChange={e => setForm({ ...form, gst_slab: e.target.value })}>
                {GST_SLABS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={cancel} className="px-3 py-2 border border-[color:var(--aii-border)] rounded text-sm">Cancel</button>
            <button onClick={submit} data-testid="pricing-save"
              className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark text-sm font-semibold flex items-center gap-1.5">
              <Save size={13}/> {editingId ? "Save changes" : "Create"}
            </button>
          </div>
        </div>
      )}

      <div className="aii-card overflow-x-auto">
        <table className="aii-table">
          <thead><tr>
            <th>Partner</th><th>Country · City</th><th>Program</th><th>Duration</th><th>Price</th><th>GST</th><th></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[color:var(--aii-muted)]">No pricing rules yet — click <b>New rule</b> to create your first one.</td></tr>}
            {filtered.map(r => (
              <tr key={r.id} data-testid={`pricing-row-${r.id}`}>
                <td className="font-medium">{r.partner_name || <span className="text-[color:var(--aii-muted)] italic">{(r.partner_id || "").slice(0, 8)}</span>}</td>
                <td>{r.destination}{r.city ? ` · ${r.city}` : " · Any city"}</td>
                <td>{r.program}</td>
                <td>{r.duration_weeks} wk</td>
                <td className="font-semibold text-brand">{SYM[r.currency] || r.currency}{Number(r.price || 0).toLocaleString()}</td>
                <td className="text-xs" data-testid={`pricing-gst-${r.id}`}>{GST_LABEL[r.gst_slab || "nil"] || "Nil"}</td>
                <td className="text-right whitespace-nowrap">
                  <div className="inline-flex gap-1">
                    <button onClick={() => startEdit(r)} className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded flex items-center gap-1"><Pencil size={11}/> Edit</button>
                    <button onClick={() => del(r.id)} data-testid={`pricing-del-${r.id}`} className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded flex items-center gap-1"><Trash2 size={11}/> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
